// resultados.jsx
import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import NarrativeContent from "../components/Resultados/NarrativaContent";
import ResultadoContent from "../components/Resultados/ResultadoContent";
import TradeoffContent from "../components/Resultados/TradeoffContent";
import iconReportes from "../assets/icons/file-download.svg";
import Select from "../components/SelectScenarios";
import { useAuth } from "../auth/AuthContext";

function getJwtClaim(token, claimName) {
  try {
    const payload = token?.split(".")[1];
    if (!payload) return null;

    const base64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedBase64 = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const bytes = Uint8Array.from(window.atob(paddedBase64), (char) =>
      char.charCodeAt(0),
    );
    return JSON.parse(new TextDecoder().decode(bytes))?.[claimName] ?? null;
  } catch {
    return null;
  }
}

function PathwayMenu({
  activeTab,
  onChangeTab,
  onExport,
  onCalculator,
  calculatorRunning,
}) {
  const tabs = ["Narrative", "Charts", "Trade-offs"];

  return (
    <div className="flex flex-wrap items-center justify-end gap-3">
      <div className="inline-flex w-fit rounded-2xl border border-[#E5E7EB] bg-[#F9FAFB] p-1 shadow-sm">
        {tabs.map((label) => {
          const isActive = activeTab === label;
          return (
            <div key={label} className="px-0.5">
              <button
                type="button"
                onClick={() => onChangeTab(label)}
                className={`rounded-xl border px-4 py-2 text-sm font-medium transition ${
                  isActive
                    ? "bg-[#03203d] text-white shadow-sm"
                    : "bg-white text-slate-600 hover:text-slate-900"
                }`}
              >
                {label}
              </button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onExport}
        className="flex items-center gap-2 rounded-md bg-[#03203d] px-4 py-2 text-white transition duration-150 hover:bg-gray-800"
      >
        Export <img src={iconReportes} alt="icon" className="h-5 w-5" />
      </button>

      <button
        type="button"
        onClick={onCalculator}
        disabled={calculatorRunning}
        className="flex items-center gap-2 rounded-md bg-[#03203d] px-4 py-2 text-white transition duration-150 hover:bg-gray-800"
      >
        Calculator <img src={iconReportes} alt="" className="h-5 w-5" />
      </button>
    </div>
  );
}

export default function Resultados() {
  const { csrfToken, accessToken, user, authFetch } = useAuth();

  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem("results_last_tab");
    return ["Narrative", "Charts", "Trade-offs"].includes(saved)
      ? saved
      : "Narrative";
  });

  useEffect(() => {
    localStorage.setItem("results_last_tab", activeTab);
  }, [activeTab]);

  const [pathways, setPathways] = useState([]);
  const [loadingPathways, setLoadingPathways] = useState(false);
  const [selectedPathwayId, setSelectedPathwayId] = useState("");

  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [calculatorRunning, setCalculatorRunning] = useState(false);
  const [calculatorTaskStatus, setCalculatorTaskStatus] = useState("");

  useEffect(() => {
    if (!accessToken) return;

    const controller = new AbortController();

    const fetchPathways = async () => {
      try {
        setLoadingPathways(true);

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/sendtask/pathways`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            },
            credentials: "include",
            signal: controller.signal,
          },
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || `Error loading pathways: ${res.status}`);
        }

        const list = data.pathways || [];
        setPathways(list);

        if (list.length > 0 && !selectedPathwayId) {
          setSelectedPathwayId(String(list[0].id));
        }
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching pathways:", err);
      } finally {
        setLoadingPathways(false);
      }
    };

    fetchPathways();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [csrfToken, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    if (!selectedPathwayId) {
      setResultsData(null);
      return;
    }

    const controller = new AbortController();

    const fetchResults = async () => {
      try {
        setLoadingResults(true);

        const params = new URLSearchParams();
        params.append("pathwayId", selectedPathwayId);

        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/api/results/results?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
              ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            },
            credentials: "include",
            signal: controller.signal,
          },
        );

        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || `Error loading results: ${res.status}`);
        }

        setResultsData(data);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error fetching results:", err);
        setResultsData(null);
      } finally {
        setLoadingResults(false);
      }
    };

    fetchResults();
    return () => controller.abort();
  }, [selectedPathwayId, csrfToken, accessToken]);

  const resultsObj = useMemo(() => {
    if (!resultsData) return null;

    const raw = Array.isArray(resultsData) ? (resultsData[0] ?? null) : resultsData;
    if (!raw || typeof raw !== "object") return null;

    const entries = Object.entries(raw);
    const [pathwayName, pathwayObject] = entries[0] ?? [];

    if (!pathwayName || !pathwayObject || typeof pathwayObject !== "object") {
      return raw;
    }

    const nextPathwayObject = {
      ...pathwayObject,
      id: selectedPathwayId,
      user_id: user?.id,
    };

    if (Array.isArray(nextPathwayObject.scenarios)) {
      nextPathwayObject.scenarios = nextPathwayObject.scenarios.map((s) => ({
        ...s,
        label: s.id,
      }));
    }

    return {
      ...raw,
      [pathwayName]: nextPathwayObject,
    };
  }, [resultsData, selectedPathwayId, user?.id]);

  const handleExport = () => {
    if (!resultsObj) {
      Swal.fire({
        icon: "info",
        title: "Info",
        text: "No results to export.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    const blob = new Blob([JSON.stringify(resultsObj, null, 2)], {
      type: "application/json;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;

    const pw = pathways.find((p) => String(p.id) === String(selectedPathwayId));
    const safeName = (pw?.name || "results")
      .replace(/[^\w\-]+/g, "_")
      .slice(0, 60);

    link.download = `${safeName}.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleCalculator = async () => {
    if (!resultsObj) {
      Swal.fire({
        icon: "info",
        title: "Info",
        text: "Select a pathway with results before running the calculator.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    setCalculatorRunning(true);
    setCalculatorTaskStatus("Creating task...");

    try {
      const createResponse = await authFetch("/api/sendtask/tasktodo", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          task_id: 15,
          name:
            pathways.find((pathway) => String(pathway.id) === selectedPathwayId)
              ?.name || "Calculator",
          status: "todo",
          onpc: 1,
          colname: "Calculator",
          colvalue: resultsObj,
        }),
      });
      const createData = await createResponse.json().catch(() => ({}));

      if (!createResponse.ok) {
        throw new Error(createData?.error || `Error creating task (${createResponse.status})`);
      }

      const taskId = createData?.id;
      if (!taskId) throw new Error("The server did not return a task id.");

      while (true) {
        const statusResponse = await authFetch(
          `/api/sendtask/tasktodo/${taskId}/status`,
          { method: "GET" },
        );
        const statusData = await statusResponse.json().catch(() => ({}));

        if (!statusResponse.ok) {
          throw new Error(statusData?.error || `Error checking task (${statusResponse.status})`);
        }

        const status = String(statusData?.status || "").toLowerCase();
        setCalculatorTaskStatus(status || "pending");

        if (status === "done") break;
        if (["error", "failed", "cancelled"].includes(status)) {
          throw new Error(`Calculator task ended with status: ${status}`);
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      const country = user?.country;
      const fileCountry =
        user?.fileCountry ||
        user?.file_country ||
        getJwtClaim(accessToken, "fileCountry");
      if (!country || !fileCountry) {
        throw new Error("Country information is missing; the download link cannot be created.");
      }

      const downloadsUrl =
        `https://cloud.scenathon.org/downloads/${encodeURIComponent(country)}` +
        `/${encodeURIComponent(fileCountry)}`;

      window.location.assign(downloadsUrl);
    } catch (error) {
      console.error("Error running calculator task:", error);
      Swal.fire({
        icon: "error",
        title: "Calculator error",
        text: error.message || "Could not complete the calculator task.",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setCalculatorRunning(false);
      setCalculatorTaskStatus("");
    }
  };

  const pathwayOptions = pathways.map((p) => ({
    value: String(p.id),
    label: `${p.name}`,
  }));
  
  if (loadingPathways && pathways.length === 0) {
    return (
      <div className="relative min-h-screen w-full p-4 font-montserrat md:p-6">
        Loading pathways...
      </div>
    );
  }

  return (
    <div className="relative min-h-screen w-full p-4 font-montserrat md:p-6">
      <div className="mb-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm md:p-5">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex flex-col gap-2 md:flex-row md:items-center">
            <label className="text-[14px] font-medium text-[#323334]">Pathways:</label>

            <div className="w-full sm:w-[300px] lg:w-[360px] max-w-full shrink-0">
              <Select
                value={selectedPathwayId || ""}
                onChange={(val) => setSelectedPathwayId(val)}
                options={pathwayOptions}
                placeholder={
                  pathways.length === 0
                    ? "There are no saved paths"
                    : "Select pathway"
                }
                disabled={loadingPathways || pathways.length === 0}
                className="w-full min-w-0"
              />
            </div>

            {loadingResults && (
              <span className="text-xs text-[#7E8081]">Loading results...</span>
            )}
          </div>

          <PathwayMenu
            activeTab={activeTab}
            onChangeTab={setActiveTab}
            onExport={handleExport}
            onCalculator={handleCalculator}
            calculatorRunning={calculatorRunning}
          />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
        {!resultsObj && selectedPathwayId && !loadingResults && (
          <div className="text-sm text-[#7E8081]">
            No results were found for this path.
          </div>
        )}

        {resultsObj && activeTab === "Narrative" && (
          <NarrativeContent resultsData={resultsObj} />
        )}

        {resultsObj && activeTab === "Charts" && (
          <ResultadoContent resultsData={resultsObj} />
        )}

        {resultsObj && activeTab === "Trade-offs" && (
          <TradeoffContent resultsData={resultsObj} />
        )}
      </div>

      {calculatorRunning && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 px-4 pb-4 pt-20 backdrop-blur-sm md:pt-24"
          role="dialog"
          aria-modal="true"
          aria-live="polite"
          aria-label="Calculator task progress"
        >
          <div className="animate-fade-in w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Calculator
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  Preparing your download
                </h2>
              </div>
              <div className="mt-1 h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-[#03203d]" />
            </div>

            <div className="mt-5 flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-600">Task status</span>
              <span className="rounded-full bg-sky-50 px-3 py-1 font-medium text-sky-800">
                {calculatorTaskStatus || "Starting..."}
              </span>
            </div>

            <div
              className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"
              role="progressbar"
              aria-label="Calculator task is running"
            >
              <div className="calculator-progress-indicator h-full rounded-full bg-sky-600" />
            </div>

            <p className="mt-3 text-xs text-slate-500">
              This may take a few moments. Please keep this page open.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
